import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { MessageResponse } from "@/components/ai-elements/message";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { packageDiffFromBaseline, unifiedLineDiff } from "@/lib/hermes-skills-diff";
import { HERMES_SKILL_ROOT_FILE } from "@/lib/hermes-skills-package";
import { cn } from "@/lib/utils";

type EditorMode = "edit" | "diff" | "preview";

type HermesSkillEditorProps = {
  selectedPath: string | null;
  content: string;
  canEdit: boolean;
  loading?: boolean;
  lastSavedFiles: Record<string, string>;
  currentFiles: Record<string, string>;
  originDiffText?: string | null;
  originDiffLoading?: boolean;
  onChange: (body: string) => void;
  onLoadOriginDiff?: () => void;
};

export function HermesSkillEditor({
  selectedPath,
  content,
  canEdit,
  loading = false,
  lastSavedFiles,
  currentFiles,
  originDiffText,
  originDiffLoading = false,
  onChange,
  onLoadOriginDiff,
}: HermesSkillEditorProps) {
  const [mode, setMode] = useState<EditorMode>("edit");

  const savedDiffText = useMemo(() => {
    if (!selectedPath) return "";
    return unifiedLineDiff(
      selectedPath,
      lastSavedFiles[selectedPath] ?? "",
      currentFiles[selectedPath] ?? "",
    );
  }, [currentFiles, lastSavedFiles, selectedPath]);

  const packageDiffText = useMemo(
    () => packageDiffFromBaseline(currentFiles, lastSavedFiles),
    [currentFiles, lastSavedFiles],
  );

  const isMarkdown =
    selectedPath === HERMES_SKILL_ROOT_FILE ||
    selectedPath?.endsWith(".md") ||
    selectedPath?.endsWith(".markdown");

  if (!selectedPath) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-center">
        <p className="text-sm text-muted-foreground">从左侧选择文件开始编辑</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <p className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">{selectedPath}</p>
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="编辑器视图">
          <Button
            type="button"
            size="sm"
            variant={mode === "edit" ? "default" : "outline"}
            className="h-7 px-2 text-xs"
            onClick={() => setMode("edit")}
          >
            编辑
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "diff" ? "default" : "outline"}
            className="h-7 px-2 text-xs"
            onClick={() => setMode("diff")}
          >
            变更
          </Button>
          {isMarkdown ? (
            <Button
              type="button"
              size="sm"
              variant={mode === "preview" ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={() => setMode("preview")}
            >
              预览
            </Button>
          ) : null}
        </div>
      </div>

      {mode === "edit" ? (
        loading ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            加载中…
          </div>
        ) : (
          <Textarea
            value={content}
            readOnly={!canEdit}
            className="min-h-0 flex-1 resize-none rounded-none border-0 font-mono text-xs shadow-none focus-visible:ring-0"
            onChange={(event) => onChange(event.target.value)}
          />
        )
      ) : null}

      {mode === "diff" ? (
        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-4 p-3">
            <section>
              <h4 className="mb-1 text-xs font-medium">相对上次保存</h4>
              {savedDiffText ? (
                <pre className="overflow-x-auto rounded-md bg-muted/40 p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap">
                  {savedDiffText}
                </pre>
              ) : (
                <p className="text-xs text-muted-foreground">当前文件无未保存变更</p>
              )}
            </section>

            {packageDiffText && packageDiffText !== savedDiffText ? (
              <section>
                <h4 className="mb-1 text-xs font-medium">整个技能包</h4>
                <pre className="overflow-x-auto rounded-md bg-muted/40 p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap">
                  {packageDiffText}
                </pre>
              </section>
            ) : null}

            {onLoadOriginDiff ? (
              <section>
                <div className="mb-2 flex items-center gap-2">
                  <h4 className="text-xs font-medium">相对系统原始种子</h4>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    disabled={originDiffLoading}
                    onClick={onLoadOriginDiff}
                  >
                    {originDiffLoading ? <Loader2 className="size-3 animate-spin" /> : "加载差异"}
                  </Button>
                </div>
                {originDiffText ? (
                  <pre className="overflow-x-auto rounded-md bg-muted/40 p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap">
                    {originDiffText}
                  </pre>
                ) : (
                  <p className="text-xs text-muted-foreground">点击加载，查看与平台种子的差异</p>
                )}
              </section>
            ) : null}
          </div>
        </ScrollArea>
      ) : null}

      {mode === "preview" && isMarkdown ? (
        <ScrollArea className="min-h-0 flex-1">
          <div className={cn("prose prose-sm dark:prose-invert max-w-none p-4")}>
            <MessageResponse>{content}</MessageResponse>
          </div>
        </ScrollArea>
      ) : null}
    </div>
  );
}
